/* global document */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { readableMobileError } from './employeeRuntimeState';
import { TEST_WORKER_URL } from './testWorkerClient';

const toDocs = (snapshot) => snapshot.docs.map((document) => ({ id: document.id, ...document.data() }));
// Slow on purpose: the delegated-admin path polls the Worker, which reads
// Firestore with the service account, and the free tier caps daily reads. The
// Worker also caches these collection reads for ~20s, so a faster poll here
// would mostly return the same bytes anyway.
const ADMIN_POLL_MS = 40000;

// Live read of a configuration/operational collection.
//
// The owner and employees read Firestore directly (their security rules cover
// it). A *delegated* admin (users.role === 'admin', no owner custom claim)
// cannot read privileged collections under the rules, so for that caller we
// poll the same data through the Worker (service account) instead.
export function useCollection(name, { filters = [], enabled = true } = {}) {
  const { user, isOwner, isAdmin } = useAuth();
  const viaWorker = !!user && isAdmin && !isOwner;
  const key = JSON.stringify(filters);

  const ref = useMemo(() => {
    if (viaWorker) return null;
    const base = collection(db, name);
    return filters.length ? query(base, ...filters.map((f) => where(f.field, f.op, f.value))) : base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, key, viaWorker]);

  const [state, setState] = useState({ docs: [], loading: enabled, error: null });

  // Direct Firestore subscription (owner / employee).
  useEffect(() => {
    if (viaWorker || !enabled) return undefined;
    setState((prev) => ({ ...prev, loading: true }));
    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => setState({ docs: toDocs(snapshot), loading: false, error: null }),
      // Keep the last-good docs on error (e.g. a transient Firestore 429) so the
      // screen degrades to slightly-stale instead of going blank.
      (error) => setState((prev) => ({ docs: prev.docs, loading: false, error: readableMobileError(error) })),
    );
    return unsubscribe;
  }, [ref, enabled, viaWorker]);

  // Worker-proxied poll (delegated admin).
  useEffect(() => {
    if (!viaWorker || !enabled) return undefined;
    let alive = true;
    const parsedFilters = JSON.parse(key);
    const tick = async () => {
      try {
        const token = await user.getIdToken();
        const res = await fetch(`${TEST_WORKER_URL}/admin/read`, {
          method: 'POST',
          headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
          body: JSON.stringify({ collection: name, filters: parsedFilters }),
        });
        if (!res.ok) throw new Error(`admin/read ${res.status}`);
        const { docs } = await res.json();
        if (alive) setState({ docs: docs || [], loading: false, error: null });
      } catch (error) {
        if (alive) setState((prev) => ({ docs: prev.docs, loading: false, error: readableMobileError(error) }));
      }
    };
    setState((prev) => ({ ...prev, loading: prev.docs.length === 0 }));
    tick();
    const guarded = () => { if (typeof document === 'undefined' || !document.hidden) tick(); };
    const id = setInterval(guarded, ADMIN_POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [viaWorker, enabled, name, key, user]);

  return state;
}

export function indexById(docs) {
  return docs.reduce((map, doc) => { map[doc.id] = doc; return map; }, {});
}
